/**
 * The time window as a value with *intent*, not a resolved pair of dates.
 *
 * The picker has always offered "Last 7 days", but the choice was thrown away:
 * a preset was resolved to concrete dates immediately, and the dropdown's
 * selection was then reverse-engineered by recomputing what each preset would
 * mean *today* and string-matching. That inference only holds while the date
 * doesn't change, so an Insights tab left open overnight silently kept
 * yesterday's window and the dropdown quietly reselected itself to "custom".
 *
 * Storing the intent instead fixes that by construction, and is the same
 * primitive a shareable link needs — a URL must say `7d`, never a pair of dates,
 * or every link freezes to the sender's day (design doc 033).
 *
 * `DateRangeFilter` stays the wire type: the API still receives
 * `{start_date, end_date}`, it just gets recomputed dates rather than remembered
 * ones. Resolution happens once, memoised on a day key — never per render.
 */
import type { DateRangeFilter } from "../types";

/**
 * `rolling` carries a duration, so it means the same thing on any day.
 * `unit` exists so "last year" stays an exact calendar year, as it always was —
 * 365 days would shift the boundary by a day across a leap year.
 */
export type DateWindow =
  | { kind: "rolling"; amount: number; unit: "day" | "year" }
  | { kind: "absolute"; start?: string; end?: string }
  | { kind: "all" };

export type DatePreset =
  | "last1day"
  | "last7days"
  | "last30days"
  | "last90days"
  | "lastYear"
  | "allTime"
  | "custom";

/**
 * A local-calendar `YYYY-MM-DD` key.
 *
 * Deliberately not `toISOString()`, which converts to UTC: the old code set the
 * local end-of-day and then formatted in UTC, pushing `end_date` to tomorrow for
 * anyone west of UTC. A window the user describes in their own days should be
 * computed in their own days.
 */
export function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Today's local date key. The single input every rolling window resolves against. */
export function todayKey(now: Date = new Date()): string {
  return toDateKey(now);
}

/**
 * A canonical string for a window, independent of object key order.
 *
 * Shared by the URL codec and by view identity so the two can't disagree about
 * whether two windows are the same. Comparing the objects directly would make
 * equality depend on the order the fields happened to be written in, which is
 * the kind of thing that works until someone constructs one differently.
 *
 * "no window at all" and "all time" are deliberately distinct: a view saved
 * before windows were part of a slice has no window and inherits yours, while
 * an all-time view sets one.
 */
export function windowKey(window: DateWindow | undefined): string {
  if (!window) return "";
  if (window.kind === "all") return "all";
  if (window.kind === "rolling") {
    return `${window.amount}${window.unit === "year" ? "y" : "d"}`;
  }
  return `${window.start ?? ""}:${window.end ?? ""}`;
}

/** Parse a `YYYY-MM-DD` key back to a local-midnight Date. */
function fromDateKey(key: string): Date {
  const [year, month, day] = key.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Resolve a window against a day. This is the only place dates are computed, so
 * a rolling window read on two different days yields two different ranges while
 * the window itself never changes.
 */
export function resolveWindow(
  window: DateWindow | undefined,
  today: string,
): DateRangeFilter | undefined {
  if (!window || window.kind === "all") return undefined;

  if (window.kind === "absolute") {
    if (!window.start && !window.end) return undefined;
    return { start_date: window.start, end_date: window.end };
  }

  const start = fromDateKey(today);
  if (window.unit === "year") {
    start.setFullYear(start.getFullYear() - window.amount);
  } else {
    start.setDate(start.getDate() - window.amount);
  }
  return { start_date: toDateKey(start), end_date: today };
}

/** The window a dropdown preset stands for. `custom` has no fixed window. */
export function presetToWindow(preset: DatePreset): DateWindow | undefined {
  switch (preset) {
    case "last1day":
      return { kind: "rolling", amount: 1, unit: "day" };
    case "last7days":
      return { kind: "rolling", amount: 7, unit: "day" };
    case "last30days":
      return { kind: "rolling", amount: 30, unit: "day" };
    case "last90days":
      return { kind: "rolling", amount: 90, unit: "day" };
    case "lastYear":
      return { kind: "rolling", amount: 1, unit: "year" };
    case "allTime":
      return { kind: "all" };
    default:
      return undefined;
  }
}

/**
 * Which dropdown entry a window *is*. Exact, because the intent was stored —
 * this replaces the old date-matching guess, which went stale every midnight.
 */
export function windowToPreset(window: DateWindow | undefined): DatePreset {
  if (!window || window.kind === "all") return "allTime";
  if (window.kind === "absolute") return "custom";
  if (window.unit === "year") return window.amount === 1 ? "lastYear" : "custom";
  switch (window.amount) {
    case 1:
      return "last1day";
    case 7:
      return "last7days";
    case 30:
      return "last30days";
    case 90:
      return "last90days";
    default:
      return "custom";
  }
}

function formatDay(key: string): string {
  const date = fromDateKey(key);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year:
      date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
}

/**
 * The filter button's label. A rolling window says what it *is* ("Last 7 days")
 * rather than the dates it currently resolves to — the old label showed only
 * dates, which is part of why the overnight drift was invisible.
 */
export function windowLabel(window: DateWindow | undefined): string {
  if (!window || window.kind === "all") return "All time";

  if (window.kind === "rolling") {
    const unit = window.unit === "year" ? "year" : "day";
    return `Last ${window.amount} ${unit}${window.amount === 1 ? "" : "s"}`;
  }

  const { start, end } = window;
  if (start && end) return `${formatDay(start)} - ${formatDay(end)}`;
  if (start) return `From ${formatDay(start)}`;
  if (end) return `Until ${formatDay(end)}`;
  return "All time";
}

/**
 * Read a legacy persisted `DateRangeFilter` as a window. Anything already in a
 * user's session was resolved from *some* day we can no longer recover, so it
 * can only be treated as absolute — it stops drifting from here on.
 */
export function windowFromDateRange(
  range: DateRangeFilter | undefined,
): DateWindow | undefined {
  if (!range) return undefined;
  if (!range.start_date && !range.end_date) return { kind: "all" };
  return { kind: "absolute", start: range.start_date, end: range.end_date };
}
